<template>
  <v-container v-if="$can('browse', 'FinanceModel')">
    <v-row

      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Financing Models
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'FinanceModel')"
          color="primary"
          class="ma-0"
          @click="openEditModal()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-select
          v-model="filtersDirty.type"
          class="mb-2"
          solo
          label="Type"
          :items="financeModelTypes.data"
          :loading="financeModelTypes.loading"
          item-text="name"
          item-value="type"
          hide-details
          clearable
        />
        <v-autocomplete
          v-model="filtersDirty.country_id"
          solo
          label="Country"
          :items="countries.data"
          :loading="countries.loading"
          item-text="name"
          item-value="id"
          hide-details
          clearable
          autocomplete="off"
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Loading">-->
        <v-row
          v-if="financeModels.loading"
          class="justify-center"
        >
          <v-col
            class="pa-6 shrink"
          >
            <v-progress-circular
              :size="50"
              :width="4"
              indeterminate
            />
          </v-col>
        </v-row>
        <!--</editor-fold>-->
        <!--<editor-fold desc="Filtering no results">-->
        <v-row
          v-else-if="!financeModels.data.length && isFiltered"

          class="text-center py-2"
        >
          <v-col cols="16">
            There are no results for your search..
          </v-col>
          <v-col
            cols="16"
            class="mt-2"
          >
            <v-btn
              class="ma-0"
              small
              text
              @click="clearFilters()"
            >
              <v-icon left>
                clear
              </v-icon>
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
        <!--</editor-fold>-->
        <!--<editor-fold desc="No data message">-->
        <v-card
          v-else-if="!financeModels.data.length"
          class="radius-10 mb-6 elevation-5"
        >
          <v-card-text class="pa-6 text-center">
            <v-row>
              <v-col cols="16">
                There are no finance models at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'FinanceModel')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openModalForm()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <!--</editor-fold>-->
        <!--<editor-fold desc="Data">-->
        <v-card
          v-for="financeModel in financeModels.data"
          v-else
          :key="financeModel.id"
          class="radius-10 elevation-5 mb-6"
        >
          <v-card-text class="pa-6">
            <v-row
              no-gutters
              align="center"
              class="mb-1"
            >
              <v-col class="title">
                {{ financeModel.label }}
              </v-col>
              <v-col
                cols="auto"
                class="mr-2 subtitle-2"
              >
                <flag
                  :iso="financeModel.country.code"
                  class="mr-1"
                  :squared="false"
                />
                {{ financeModel.country.name }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  v-if="$can('edit', 'FinanceModel')"
                  small
                  class="ma-0"
                  @click.native="openEditModal(financeModel)"
                >
                  <v-icon
                    left
                    small
                  >
                    edit
                  </v-icon>
                  Edit
                </v-btn>
              </v-col>
            </v-row>

            <div class="mb-3">
              <v-chip outlined>
                Type:
                <strong class="ml-2">
                  {{ financeModel.specifications.name }}
                </strong>
              </v-chip>
              <v-chip
                v-for="param in financeModel.parameter_values"
                :key="param.field"
                outlined
              >
                {{ param.label }}:
                <strong class="ml-2">
                  {{ param.value }} {{ param.unit }}
                </strong>
              </v-chip>
            </div>

            <div>
              {{ financeModel.note }}
            </div>
          </v-card-text>
        </v-card>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <finance-model-edit-dialog
      :visible.sync="editModal"
      :finance-model-types="financeModelTypes"
      :finance-model="financeModelDirty"
      @update:financeModels="fetchFinanceModels"
      @financeModel:created="financeModelCreated"
      @financeModel:updated="financeModelUpdated"
      @financeModel:deleted="financeModelDeleted"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import { mapGetters } from 'vuex'
  import filterableMixin from '@/mixins/filterableMixin'
  import FinanceModel from '@/models/FinanceModel'
  import FinanceModelEditDialog from '@/components/settings/financeModels/FinanceModelEditDialog'

  export default {
    name: 'FinanceModels',
    components: { FinanceModelEditDialog },
    mixins: [filterableMixin],
    data() {
      return {
        financeModels: {
          data: [],
          loading: false
        },
        financeModelDirty: undefined,
        financeModelTypes: {
          data: [],
          loading: false
        },
        filtersClean: FinanceModel.emptyFilters,
        filtersDirty: cloneDeep(FinanceModel.emptyFilters),
        editModal: false
      }
    },
    computed: {
      ...mapGetters('settings', ['countries'])
    },
    created() {
      this.fetchFinanceModelTypes()
      this.fetchFinanceModels()
    },
    methods: {
      onFiltersUpdate() {
        this.fetchFinanceModels()
      },
      async fetchFinanceModels() {
        this.financeModels.loading = true

        let queryParams = {
          ...this.filterQueryParams
        }

        this.financeModels.data = await FinanceModel.api.getAll(queryParams)
        this.financeModels.loading = false
      },
      openEditModal(financeModel = null) {
        if (!financeModel) this.financeModelDirty = undefined
        else this.financeModelDirty = financeModel

        this.editModal = true
      },
      clearFilters() {
        this.filters = cloneDeep(FinanceModel.emptyFilters)
      },
      async fetchFinanceModelTypes() {
        this.financeModelTypes.loading = true

        this.financeModelTypes.data = await FinanceModel.api.getTypes()

        this.financeModelTypes.loading = false
      },
      financeModelCreated(financeModel) {
        this.financeModels.data.unshift(financeModel)
      },
      financeModelUpdated(financeModel) {
        const index = this.financeModels.data.findIndex(fm => fm.id === financeModel.id)

        this.financeModels.data.splice(index, 1, financeModel)
      },
      financeModelDeleted(financeModelId) {
        const index = this.financeModels.data.findIndex(fm => fm.id === financeModelId)

        this.financeModels.data.splice(index, 1)
      }
    }
  }
</script>
